import { TitleCasePipe } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';

import moment from 'moment-timezone';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Observable, Subscription, NEVER } from 'rxjs';
import { distinctUntilChanged, filter, catchError, switchMap, tap, take } from 'rxjs/operators';

import { MediaServiceApi } from '../../../../api/mediaService/media-service.api';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { LinkedinSubTypeEnum } from '../../../Basic/enums/linkedin-sub-type.enum';
import { TVEyesArchivedStateEnum } from '../../../../api/mentions/enum/tv-eyes-archived-state.enum';
import { MentionsApi } from '../../../../api/mentions/mentions.api';
import { PartialMentionApiModel } from '../../../../api/mentions/model/mention.model';
import { ProminenceRecalculationJobStatus } from '../../../../api/monitoring/monitoring-account-configuration.api';
import { OEmbedRichResponseModel } from '../../../../api/oembed/models/oembed-rich-response.model';
import { OEmbedApi } from '../../../../api/oembed/oembed.api';
import { IndexHighlightPipe } from '../../../../Common/Misc/pipes/Highlight/indexHighlight.pipe';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { AccountModel } from '../../../../Core/Users/models/account.model';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { CurrentUserStateService, TargetingAccessLevel } from '../../../../Core/Users/services/current-user-state.service';
import { FolderService } from '../../../../Monitor/Core/services/folder.service';
import { MentionsService } from '../../../../Monitor/Core/services/mentions.service';
import { TopicsService } from '../../../../Monitor/Core/services/topics.service';
import { EngagementAttributeTypesEnum } from '../../../../Monitor/Shared/enums/engagement-attribute-types.enum';
import { mentionAttributeTypesEnum } from '../../../../Monitor/Shared/enums/mention-attribute-types.enum';
import { CriticalMentionMessageModel } from '../../../../Monitor/Shared/models/critical-mention-message.model';
import { Folder } from '../../../../Monitor/Shared/models/folder.model';
import { MentionSearchModel } from '../../../../Monitor/Shared/models/mention-search.model';
import { ProminenceLevel, ProminenceScoreModel } from '../../../../Monitor/Shared/models/prominence-score.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { JournodexGeographyModel } from '../../../Journodex/models/journodex-geography.model';
import { attributeTypesEnum } from '../../../Misc/enums/attribute-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { download } from '../../../Misc/helpers/download/download.helper';
import { AttributeModel } from '../../../Misc/models/attribute.model';
import { KeywordHighlightModel } from '../../../Misc/models/keyword-highlight.model';
import { ProminenceLockerService } from '../../../Misc/services/prominence-locker.service';
import { TagsService } from '../../../Tags/services/tags.service';
import { Mention } from '../../models/mention';
import { TwitterSubTypeEnum } from '@Common/Basic/enums/twitter-sub-type.enum';
import { sortByDate } from '../../../Misc/helpers/sort/sort.helper';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { TranslateMentionsService } from '../../../Mentions/services/translate-mentions.service';
import { criticalMentionMessageTypesEnum } from '../../../../Monitor/Shared/enums/critical-mention-message-types.enum';
import { IframeDialogComponent } from '../../../Dialogs/components/IframeDialog/iframe-dialog.component';
import { TextInputDialogComponent } from '../../../Dialogs/components/TextInputDialog/text-input-dialog.component';
import { IframeDialogModel } from '../../../Dialogs/models/iframe-dialog.model';
import { TextInputDialogModel } from '../../../Dialogs/models/text-input-dialog.model';
import { MentionInsightModel } from '../../models/mention-insight.model';
import { ResourceActionTypes } from '../../../Basic/enums/resource-actions.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { MentionWorkbenchDialogComponent, MentionWorkbenchTabIds } from '../MentionWorkbenchDialog/mention-workbench-dialog.component';
import { MediaApi } from '../../../../api/contentLibrary/models/content-library-media.api.model';
import { ManageMentionOrganizationDialogComponent } from '../ManageMentionOrganization/manage-mention-organization-dialog.component';
import { ProminenceTakeOverPanelComponent } from '../../../../Account/AccountPreferences/components/ProminenceTakeOverPanel/prominence-take-over-panel.component';
import { ImageModel } from '../../../Misc/models/image.model';
import { ViewerUrlTypeEnum } from '@Common/Mentions/enums/viewer-url-type.enum';

enum TranslateTabIds {
    ORIGINAL,
    TRANSLATED
}

@Component({
    selector: 'agpr-mention-details',
    templateUrl: './mention-details.component.html',
    styleUrls: ['./mention-details.component.scss'],
    providers: [{
        provide: MediaServiceApi,
        useClass: MediaServiceApi
    }],
    standalone: false
})
export class MentionDetailsComponent implements OnDestroy, OnInit {

    @Input() id: string;
    @Input() highlightKeywords: KeywordHighlightModel[];
    @Input() hasProminenceProvision: boolean;
    @Input() showInsights: boolean = false;
    @Input() showImageList: boolean = false;
    @Input() isDetailsTakeOverPanel: boolean = false;

    @Output() outletClicked: EventEmitter<void> = new EventEmitter<void>();
    @Output() contactClicked: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('prominenceAttributeTemplate') prominenceAttributeTemplate: TemplateRef<any>;

    overlayContainer: Element;
    preferences: PreferencesModel;
    account: AccountModel;
    user: UserModel;
    mention: Mention = null;

    mentionAttributes: AttributeModel[] = [];
    mentionInformation: AttributeModel[] = [];
    engagementInformation: AttributeModel[] = [];
    mentionProminence: AttributeModel[] = [];
    dateFormat: string;

    bodyParagraphs: string[] = [];
    summaryParagraphs: string[] = [];
    notesParagraphs: string[] = [];

    folders: Folder[] = [];

    isCriticalMention: boolean;
    isLegacyStashedClip: boolean;
    isStashedClip: boolean;
    isCriticalMentionValid: boolean;
    criticalMentionFeedbackMessage: string;
    iframeDialogKey: string;

    criticalMentionSlimPlayerURL: string;
    isSocialFrameLoaded: boolean;
    isLegacyTargetingUser: boolean;

    socialEmbedURL: string;
    socialEmbedCode: SafeHtml;

    search: MentionSearchModel;

    currentYear: number = new Date().getFullYear();
    lexisNexusResourceUrl: SafeResourceUrl;

    window = window;

    attachments: MediaApi[] = [];

    prominenceStatus: ProminenceRecalculationJobStatus;
    noProminenceContactLink: string;

    iframeTitle: string;
    insights: MentionInsightModel[] = [];

    selectedTabIndex: number = 0;
    tabIds = TranslateTabIds;

    openAiPermission: boolean = false;
    isResellerAccount: boolean = false;
    hasAdvancedMonitoringPermissions: boolean = false;

    private subscriptions: Subscription[] = [];
    private hasExpiryDateAttribute: boolean;
    private regionRequest: Subscription;
    private initialTagIds: number[];
    private titleCasePipe = new TitleCasePipe();

    readonly viewerUrlTypeEnum = ViewerUrlTypeEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly tvEyesState = TVEyesArchivedStateEnum;
    readonly prominenceStatuses = ProminenceRecalculationJobStatus;
    readonly endOfUrl = /([^\/]+$)/g;
    readonly tweetTypeEnum = TwitterSubTypeEnum;
    readonly linkedinTypeEnum = LinkedinSubTypeEnum;
    readonly mentionAttributeTypesEnum = mentionAttributeTypesEnum;
    readonly attributeTypesEnum = attributeTypesEnum;

    private readonly expiryDateFormat = 'MMMM D, YYYY';
    private readonly totalValidDaysForCriticalMention: number = 60;

    constructor(
        private configurationService: ConfigService,
        public mentionsApi: MentionsApi,
        private folderService: FolderService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private mentionsService: MentionsService,
        private sanitizer: DomSanitizer,
        private oembedApi: OEmbedApi,
        private coreDialogService: CoreDialogService,
        private currentUserStateService: CurrentUserStateService,
        private topicsService: TopicsService,
        private tagsService: TagsService,
        private mediaServiceApi: MediaServiceApi,
        public httpClient: HttpClient,
        private prominenceLockerService: ProminenceLockerService,
        private cdr: ChangeDetectorRef,
        protected indexHighlightPipe: IndexHighlightPipe,
        protected translateService: TranslateMentionsService,
        public injector: Injector
    ) {
        this.isResellerAccount = configurationService.hasBrandGUID();
        this.overlayContainer = document.getElementById('main-content');
    }

    ngOnInit(){
        this.subscriptions.push(
            this.prominenceLockerService.prominenceStatus$.subscribe(status => this.prominenceStatus = status),
            this.mentionsService.getFocusedMention().pipe(
                switchMap((focusedMention) => this.folderService.getFoldersByMention(focusedMention)),
                filter(x => !!x)
            ).subscribe(folders => {
                this.folders = folders.sort(sortByDate);
            }),
            this.mentionsService.getFocusedMention().pipe(distinctUntilChanged()).subscribe(
                mention => {
                    if (!mention) {
                        this.mention = null;
                        this.isCriticalMention = false;
                        this.criticalMentionSlimPlayerURL = '';
                        this.socialEmbedURL = undefined;
                        this.socialEmbedCode = undefined;
                        this.isSocialFrameLoaded = false;
                        this.lexisNexusResourceUrl = null;
                        this.bodyParagraphs = [];
                        this.summaryParagraphs = [];
                        this.notesParagraphs = [];
                        this.initialTagIds = null;
                        this.insights = [];
                    } else {
                        let reloadSocial = false;
                        if (!this.mention || mention.id !== this.mention.id) {
                            this.trackMention(mention);
                        }
                        if (!this.mention || this.mention.url !== mention.url) {
                            this.isSocialFrameLoaded = false;
                            reloadSocial = true;
                            this.cdr.detectChanges();
                        }
                        this.mention = mention;
                        this.iframeTitle = this.localizationService.transform(
                            'Mention.details.iframeTitle',
                            {media: this.titleCasePipe.transform(this.mention.mediaType.id)}
                        );
                        const highlightedBody = this.indexHighlightPipe.transform(mention.body, mention.contexualKeywordIndices, this.highlightKeywords);
                        this.bodyParagraphs = (highlightedBody || '').split('\n').filter(x => x !== '');
                        this.summaryParagraphs = (mention.summary || '').split('\n').filter(x => x !== '');
                        this.notesParagraphs = (mention.note || '').split('\n').filter(x => x !== '');
                        this.initialTagIds = [...mention.tagIds];
                        this.setAttributes();
                        this.setCriticalMentionFeatures();
                        this.loadAttachments();
                        if (reloadSocial) {
                            this.socialEmbedCode = undefined;
                            this.socialEmbedURL = this.mention.getEmbedURL();
                            if (!this.socialEmbedURL) {
                                this.getOEmbedCode();
                            }
                        }

                        // because the translated tab is disabled just before this, it seems to ignore switching tabs automatically, the setTimeout fixes it
                        setTimeout(() => {
                            if (this.mention?.isTranslatedPreferred && this.mention.translation) {
                                this.selectedTabIndex = 1;
                            } else {
                                this.selectedTabIndex = 0;
                            }
                        }, 0);

                        if (this.mention.insights?.length) {
                            this.insights = [...this.mention.insights];
                            this.insights.sort((a, b) => {
                                if (a.isPreferred) {
                                    return -1;
                                }

                                if (b.isPreferred) {
                                    return 1;
                                }

                                return 0;
                            });
                        } else {
                            this.insights = [];
                        }
                    }
                }
            ),
            this.currentUserStateService.getTargetingAccessLevel().subscribe(x => {
                this.isLegacyTargetingUser = x === TargetingAccessLevel.CLASSIC_ONLY;
            }),
            this.topicsService.getFocusedTopic().subscribe(
                search => {
                    this.search = search;
                }
            ),
            this.localizationService.getLocalizationMapChange().subscribe(() => this.setAttributes()),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                prefs => {
                    this.preferences = prefs;
                    this.dateFormat = prefs.getDateFormatString(true, true, true);

                    if (this.mentionInformation && this.mentionInformation.length) {
                        this.addExpiryDateAttribute();
                    }
                    this.setCriticalMentionFeatures();
                }
            ),
            this.currentUserStateService.getAccount().subscribe(
                account => {
                    this.account = account;
                }
            ),
            this.currentUserStateService.getUser().subscribe(
                user => {
                    this.user = user;
                }
            ),
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.openAiPermission = permissions.includes(userPermissionsEnum.openAI);
                    this.hasAdvancedMonitoringPermissions = permissions.includes(userPermissionsEnum.advancedMonitoring);
                }
            }),
        );
        this.setNoProminenceContactLink();
    }


    removeMentionFromFolder(folder: Folder) {
        if (this.mention) {
            this.folderService.removeMentionsFromFolder([this.mention], folder).subscribe();
        }
    }




    isSlimPlayerValid(): boolean {
        let slimPlayerMatchUrl;

        if (!this.mention.url) {
            return false;
        }

        if (this.isStashedClip) {
            slimPlayerMatchUrl = this.mention.url.match(/\#\/clip\/slim\/.*/);
        } else {
            slimPlayerMatchUrl  = this.mention.url.match(/^https:\/\/([^\.]+)\.criticalmention\.com\/.*#\/clip\/search\/([^\?]+)?.*&token=([^\&]+?)(&|$)/);
        }

        return slimPlayerMatchUrl;
    }


    /**
     * Opens critical mention wordplay dialog
     */
    editCriticalMention(): void {
        const styleOverrideURL = `https://${window.location.hostname}/app/assets/css/critical-mention-override.css`;

        const iframeConfig: IframeDialogModel = {
            inputs: {
                id: 'mention.wordplayEditor',
                heading: 'Mention.details.wordplayEditor',
                url: `${this.mention.criticalMentionWordPlayUrl}&ess=${styleOverrideURL}`
            },
            outputs: {
                message: (message) => {
                    this.handleCriticalMentionMessage(message);
                }
            }
        };
        this.iframeDialogKey = this.coreDialogService.open(IframeDialogComponent, iframeConfig);
    }


    setMentionProminenceAttributes(): void {
        this.mentionProminence = [];
        if (this.mention.prominenceScores.length > 0) {
            let prom: AttributeModel;
            this.mention.prominenceScores.forEach(score => {
                if (score.prominenceLevel !== ProminenceLevel.NONE) {
                    // Extract the last word of the of the subject name so that we can
                    // wrap it with the tooltip icon to prevent icon orphaning by line breaks
                    // If the subject name is only one word then label is empty and the
                    // name is stored in customLabel
                    const label = score.prominenceGroupName.split(' ');
                    const lastWord = label.splice(label.length - 1);
                    prom = {
                        id: null,
                        label: label.join(' '),
                        value: ProminenceScoreModel.getProminenceLabel(score.prominenceLevel),
                        type: attributeTypesEnum.prominence,
                        iconToolTip: score.keywords.toString(),
                        customTemplate: this.prominenceAttributeTemplate,
                        customLabel: lastWord
                    };
                    this.mentionProminence.push(prom);
                }
            });
        }
    }



    addMentionToFolder(folder: Folder): void {
        if (this.mention) {
            this.folderService.saveMentionsToFolder([this.mention], folder).subscribe();
        }
    }

    /**
     * Handles change to the tags selected for a mention
     */
    tagsUpdated(tagIds: number[]): void {
        this.mention.tagIds = tagIds;
        const cachedTagIds = [...this.initialTagIds];

        const patch: PartialMentionApiModel = {
            id: this.mention.mentionId,
            tagIds: this.mention.tagIds
        };

        this.mentionsService.patchAndReturnMention(patch).pipe(
            catchError(err => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Monitor.tags.updateErrorHeading',
                    detail: 'Monitor.tags.updateErrorMessage'
                });

                return NEVER;
            }),
            switchMap((response) => {
                this.mentionsService.setFocusedMention(response);

                if (cachedTagIds.length < response.tagIds.length) {
                    const uncachedIds = response.tagIds.filter(id => !cachedTagIds.includes(id));
                    return this.tagsService.getTagsByIds(uncachedIds).pipe(
                        filter(x => !!x),
                        tap(tags => {
                            tags.forEach(tag => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: this.localizationService.transform('Monitor.tags.addedSuccessHeading', {
                                        tagName: tag.name
                                    }),
                                    detail: this.localizationService.transform('Monitor.tags.addedSuccessMessage', {
                                        tagName: tag.name
                                    })
                                });
                            });
                        }),
                        take(1)
                    );
                } else {
                    const unsavedTagIds = cachedTagIds.filter(id => !response.tagIds.includes(id));
                    return this.tagsService.getTagsByIds(unsavedTagIds).pipe(
                        filter(x => !!x),
                        tap(tags => {
                            tags.forEach(tag => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: this.localizationService.transform('Monitor.tags.removedSuccessHeading', {
                                        tagName: tag.name
                                    }),
                                    detail: this.localizationService.transform('Monitor.tags.removedSuccessMessage', {
                                        tagName: tag.name
                                    })
                                });
                            });
                        }),
                        take(1)
                    );
                }
            })
        ).subscribe();
    }

    /**
     * Tracks a mention as viewed in the api
     */
    trackMention(mention: Mention): void {
        // Only track mentions that have a mercuryId
        if (mention.id) {
            this.mentionsApi.trackMentionView(mention.convertToApiMention()).subscribe();
        }
    }

    openManageMentionOrganizationDialog() {
        this.coreDialogService.open(ManageMentionOrganizationDialogComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                )
            }
        });
    }


    loadAttachments(): void {
        this.attachments = [];

        if (this.mention.attachments.length > 0) {
            this.mediaServiceApi.getFiles(this.mention.attachments).subscribe(mediaResults => {
                mediaResults.mediaResults.forEach(mediaResult => {
                    this.attachments.push({
                        id: mediaResult.id,
                        name: mediaResult.name,
                        url: mediaResult.url,
                        mimeType: mediaResult.mimeType,
                        size: mediaResult.size,
                    });
                });
            });
        }
    }

    downloadAttachment(attachment: MediaApi): void {
        let headers = new HttpHeaders;
        headers = headers.set('content-type', attachment.mimeType);
        this.httpClient.get(attachment.url, {'headers': headers, responseType: 'blob'}).subscribe(
            result => {
                download.blob(result, attachment.name);
            }
        );
    }

    setNoProminenceContactLink(): void {
        if (this.configurationService.brandGUIDIsBurrelles()) {
            this.noProminenceContactLink = 'mailto:inquiry@burrelles.com';
        } else {
            this.noProminenceContactLink = 'https://www.agilitypr.com/contact/';
        }
    }


    openProminenceTakeOverPanel(): void {
        this.coreDialogService.open(ProminenceTakeOverPanelComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                )
            }
        });
    }

    tabChanged(tabIndex: number) {
        this.selectedTabIndex = tabIndex;
    }

    updateInsight(insight: MentionInsightModel): void {
        this.persistInsights(insight.name, ResourceActionTypes.UPDATE);
    }

    deleteInsight(insight: MentionInsightModel): void {
        this.insights = this.insights.filter(item => item.id !== insight.id);
        if (this.insights.length === 1) {
            // if there is only one insight, by default, it is the preferred one
            this.insights[0].isPreferred = true;
        }

        this.persistInsights(insight.name, ResourceActionTypes.DELETE);
    }

    preferInsight(insight: MentionInsightModel): void{
        this.insights.forEach(item => {
            if (item.id === insight.id) {
                item.isPreferred = true;
            } else {
                item.isPreferred = false;
            }
        });

        this.persistInsights(insight.name, ResourceActionTypes.UPDATE);
    }

    persistInsights(name: string, actionType: ResourceActionTypes): void {
        const patch: PartialMentionApiModel = {
            id: this.mention.mentionId,
            insights: this.insights?.map(insight => insight.toApiModel())
        };

        this.mentionsService.updateMentionInsights(patch, name, actionType).pipe().subscribe();
    }

    createNewInsight() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const ioModel =  {
            inputs: {
                animationTrigger: animationTrigger,
                mention: this.mention,
                initialTab: MentionWorkbenchTabIds.INSIGHTS
            }
        };

        this.coreDialogService.open(MentionWorkbenchDialogComponent, ioModel, this.injector);
    }


    updatePreferredImage(image: ImageModel) {
        const images = this.mention.images.map(img => {
            const imgApiModel = img.toApiEntity();
            imgApiModel.isPreferred = img.source === image.source ? !image.isPreferred : false;
            return imgApiModel;
        });

        const patch: PartialMentionApiModel = {
            id: this.mention.mentionId,
            images
        };

        this.mentionsService.patchMention(patch);
    }

    unlockMentionView(): void {
        this.window.open('https://www.agilitypr.com/contact/', '_blank');
    }

    /**
     * Handles messages sent by the Critical Mention player to the parent window
    */
    private handleCriticalMentionMessage(message: CriticalMentionMessageModel): void {
        const hasMessage: boolean = message !== null && typeof message === 'object';
        const isSavingClip: boolean = hasMessage && message.action === criticalMentionMessageTypesEnum.create;
        const isUpdatingClip: boolean = hasMessage && message.action === criticalMentionMessageTypesEnum.update;

        if (isSavingClip) {
            this.saveCriticalMentionClip(message);
        } else if (isUpdatingClip) {
            this.updateCriticalMentionClip(message);
        }
    }

    /**
     * Begins the process of updating a mention based on a clip that
     * was archived in Critical Mention's Wordplay player
    */
    private updateCriticalMentionClip(message: CriticalMentionMessageModel): void {
        const mention: Mention = this.mention.clone();
        mention.body = message.text;
        mention.duration = Math.round((Number(message.endSecondMicro) - Number(message.startSecondMicro)) / 1000);
        this.mentionsService.upsertMention(mention);
        this.coreDialogService.close(this.iframeDialogKey);
    }

    /**
     * Saves a mention based on a clip that has been archived
     * in Critical Mention's Wordplay player
    */
    private saveCriticalMentionClip(message: CriticalMentionMessageModel): void {
        const mention: Mention = this.mention.clone();
        mention.body = message.text;
        mention.duration = Math.round((Number(message.endSecondMicro) - Number(message.startSecondMicro)) / 1000);
        mention.criticalMentionWordPlayUrl = this.getCriticalMentionWordPlayUrl(message);

        const io: TextInputDialogModel = {
            inputs: {
                heading: 'MentionDetails.saveClip',
                initialValue: mention.headline,
                okLabel: 'save',
                submit: (headline: string) => {
                    mention.headline = headline;

                    const saveRequest = this.mentionsService.upsertMention(mention, this.search);
                    saveRequest.subscribe(() => {
                        this.coreDialogService.close(this.iframeDialogKey);
                    });

                    return saveRequest;
                }
            }
        };

        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    /**
     * Gets the WordPlay URL for a given clip based on the provided
     * critical mention archive clip confirmation message
    */
    private getCriticalMentionWordPlayUrl(message: CriticalMentionMessageModel): string {
        const url = new URL(this.mention.criticalMentionWordPlayUrl);
        const shareUUIDMatches = message.directLink.match(/\/slim\/(.*)\?/);

        if (shareUUIDMatches) {
            // take the first group
            url.searchParams.append('shareUUID', shareUUIDMatches[1]);
        }

        url.searchParams.append('clipId', '' + message.clipId);

        return url.toString();
    }


    /**
     * Sets the relevant properties to display critical mention
     */
    private setCriticalMentionFeatures(): void {
        if (!this.mention) {
            return;
        }

        this.isCriticalMention = Mention.isCriticalMention(this.mention);
        this.isLegacyStashedClip = this.isCriticalMention && !this.mention.criticalMentionWordPlayUrl;
        this.isCriticalMentionValid = true;
        this.setIsStashedClip();

        if (this.isCriticalMentionExpired() && !this.isStashedClip) {
            this.isCriticalMentionValid = false;
            this.criticalMentionFeedbackMessage = 'Mention.details.criticalMentionExpired';
        }

        if (this.isCriticalMention && !this.isSlimPlayerValid()) {
            this.criticalMentionFeedbackMessage = 'Mention.details.criticalMention.generalError';
            this.isCriticalMentionValid = false;
        }

        this.setCriticalMentionSlimPlayerURL();
    }

    private isCriticalMentionExpired(): boolean {
        if (!this.preferences || !this.preferences.timeZone || !this.mention) {
            return false;
        }

        if (Mention.isCriticalMention(this.mention)) {
            const timezone = this.preferences.timeZone;
            const published = moment(new Date(this.mention.datePublished)).tz(timezone);
            const now = moment().tz(timezone);

            const expiryDateInPerferedTimezone = published.add(this.totalValidDaysForCriticalMention, 'd');

            return expiryDateInPerferedTimezone.diff(now, 'd') <= 0;
        }

        return false;
    }

    private setIsStashedClip() {
        if (!this.isCriticalMention) {
            this.isStashedClip = false;

            return;
        }

        if (this.isLegacyStashedClip) {
            this.isStashedClip = true;

            return;
        }

        if (this.mention.criticalMentionWordPlayUrl) {
            const clipIdMatches = this.mention.criticalMentionWordPlayUrl.match(/clipid/i);
            this.isStashedClip = !!clipIdMatches && !!clipIdMatches.length;
        }
    }


    /**
     * Generates and sets the URL to embed this mention's Critical Mention player
    */
    private setCriticalMentionSlimPlayerURL(): void {
        let criticalMentionURL: string = '';

        if (this.isCriticalMention) {
            // mention.url is the slim player url for critical mentions
            criticalMentionURL = this.mention.url;
        } else {

            return;
        }

        if (this.criticalMentionSlimPlayerURL !== criticalMentionURL) {
            const url = new URL(criticalMentionURL);
            // the iframe doesnt recognize changes in the url hash, adding date before hash registers change
            url.searchParams.append('_', '' + +new Date());
            this.criticalMentionSlimPlayerURL = url.toString();
        }
    }

    /**
     * Requests embedable content from social networks
     */
    private getOEmbedCode() {
        if (!this.mention.mediaType.isSocial || !this.mention.url) {
            return;
        }

        let request: Observable<OEmbedRichResponseModel>;
        switch (this.mention.mediaType.id) {
            // @TODO NEP-32721 - facebook doesn't work right now because of a CORS problem
            // case MediaTypeEnum.FACEBOOK:
            //     request = this.oembedApi.facebook(this.mention.url);
            //     break;
            case MediaTypeEnum.TWITTER:
                request = this.oembedApi.twitter(this.mention.url);
                break;
            case MediaTypeEnum.TIKTOK:
                const videoId = this.mention.url.match(this.endOfUrl)?.[0];

                // eslint-disable-next-line max-len
                const tiktokBlock: any = `<blockquote class="tiktok-embed" cite="${this.mention.url}" data-video-id="${videoId}" style="max-width: 605px;min-width: 325px;" >
                    <section>
                    </section>
                </blockquote>
                <script async src="https://www.tiktok.com/embed.js"></script>`;

                this.socialEmbedCode = this.sanitizer.bypassSecurityTrustHtml(tiktokBlock);
                break;
            case MediaTypeEnum.INSTAGRAM:
                const instagramBlock: any = `<blockquote class="instagram-media" data-instgrm-captioned="" data-instgrm-version="13">
                    <a href="${this.mention.url}" target="_blank">Open on Instagram</a>
                    </blockquote><script src="//www.instagram.com/embed.js"></script>`;

                this.socialEmbedCode = this.sanitizer.bypassSecurityTrustHtml(instagramBlock);
                break;
            case MediaTypeEnum.BLUESKY:
                request = this.oembedApi.bluesky(this.mention.url);
                break;
        }

        if (request) {
            request.subscribe((response) => {
                this.socialEmbedCode = this.sanitizer.bypassSecurityTrustHtml(response.html);
            });
        }
    }

    /**
     * Adds an Attribute to mention information to display the expiry date of content
     */
    private addExpiryDateAttribute(): void {
        if (
            !this.preferences
            || !this.preferences.timeZone
            || !this.mention
            || !Mention.isCriticalMention(this.mention)
            || !this
        ) {
            return;
        }

        this.hasExpiryDateAttribute = true;

        const timezone = this.preferences && this.preferences.timeZone;
        const publishedDateInPerferedTimezone = moment(new Date(this.mention.datePublished)).tz(timezone);
        const currentDateInPreferedTimezone = moment().tz(timezone);
        const expiryDateInPerferedTimezone = publishedDateInPerferedTimezone.add(this.totalValidDaysForCriticalMention, 'd');
        const dateDifferenceInDays = expiryDateInPerferedTimezone.diff(currentDateInPreferedTimezone, 'd');

        let expiryDate: string;

        if (dateDifferenceInDays <= 0) {
            expiryDate = this.localizationService.transform('Mention.details.expired');
        } else {
            const localizedDaysRemaining = this.localizationService.transform(
                'Mention.details.daysRemaining',
                { days: dateDifferenceInDays }
            );
            expiryDate = publishedDateInPerferedTimezone.format(this.expiryDateFormat) + ' ' + localizedDaysRemaining;
        }

        const exipryDateAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.location,
            label: 'Mention.details.expiryDate',
            value: expiryDate,
            type: attributeTypesEnum.simple,
            icon: 'fa-regular fa-circle-info',
            iconToolTip: 'Mention.details.expiryDateToolTip'
        };

        this.mentionInformation = [
            ...this.mentionInformation,
            exipryDateAttribute
        ];
    }


    private setAttributes() {
        if (!this.mention) {
            this.mentionAttributes = [];
            return;
        }

        this.setMentionInformationAttributes();
        this.setEngagementAttributes();

        if (this.hasProminenceProvision) {
            this.setMentionProminenceAttributes();
        }

        if (!this.mention.createdDate) {
            this.mentionAttributes = [];
            return;
        }

        const createdByUserAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.savedBy,
            label: 'Mention.details.savedBy',
            value: this.mention.createdByUserId,
            type: attributeTypesEnum.user,
        };

        const createdOnDateAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.savedDate,
            label: 'Mention.details.savedDate',
            value: this.mention.createdDate,
            type: attributeTypesEnum.date,
        };

        const lastModifiedByUserAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.lastModifiedBy,
            label: 'Mention.details.modifiedBy',
            value: this.mention.lastModifiedByUserId,
            type: attributeTypesEnum.user
        };

        const lastModifiedDateAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.lastModifiedDate,
            label: 'Mention.details.modifiedDate',
            value: this.mention.lastModifiedDate,
            type: attributeTypesEnum.date,
        };

        this.mentionAttributes = [
            createdByUserAttribute,
            createdOnDateAttribute,
            lastModifiedByUserAttribute,
            lastModifiedDateAttribute,
        ].filter(x => !!x.value);
    }


    private setMentionInformationAttributes() {
        if (this.regionRequest) {
            this.regionRequest.unsubscribe();
        }

        this.hasExpiryDateAttribute = false;

        this.mentionInformation = this.mentionsService.getMentionInformation(this.mention);
        this.regionRequest = this.mentionsService.getMentionRegion(this.mention)
            .subscribe((region) => {
                this.addLocationAttribute(region);
            });
    }

    private addLocationAttribute(region: JournodexGeographyModel) {
        if (this.regionRequest) {
            this.regionRequest.unsubscribe();
            this.regionRequest = null;
        }

        let regionKeyString = 'na';

        if (region) {
            regionKeyString = region.displayString;
        }

        const locationAttribute: AttributeModel = {
            id: mentionAttributeTypesEnum.location,
            label: 'Mention.details.location',
            value: regionKeyString,
            type: attributeTypesEnum.simple,
            icon: 'fa-solid fa-location-dot'
        };

        if (!this.hasExpiryDateAttribute) {
            this.mentionInformation = [
                ...this.mentionInformation,
                locationAttribute
            ];

            return;
        }

        this.mentionInformation.splice(-1, 0, locationAttribute);
    }


    private setEngagementAttributes() {
        const engagementAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.engagements,
            label: 'Mention.details.engagements',
            value: this.mention.engagements,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-retweet'
        };

        const engagementRateAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.engagementRate,
            label: 'Mention.details.engagementRate',
            value: this.mention.engagementRate,
            type: attributeTypesEnum.percent,
            icon: 'fa-solid fa-percent'
        };

        const likeIcon = (this.mention.mediaType.id === MediaTypeEnum.FACEBOOK
            || this.mention.mediaType.id === MediaTypeEnum.INSTAGRAM
            || this.mention.mediaType.id === MediaTypeEnum.YOUTUBE
            || this.mention.mediaType.id === MediaTypeEnum.TIKTOK)
            ? 'fa-solid fa-thumbs-up'
            : 'fa-solid fa-heart';

        const likesAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.likes,
            label: 'Mention.details.likes',
            value: this.mention.like,
            type: attributeTypesEnum.engagement,
            icon: likeIcon
        };

        const upvotesAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.likes,
            label: 'Mention.details.upvotes',
            value: this.mention.like,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-thumbs-up'
        };

        const commentsAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.comments,
            label: 'Mention.details.comments',
            value: this.mention.reply,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-comment'
        };

        const repliesAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.comments,
            label: 'Mention.details.replies',
            value: this.mention.reply,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-comment'
        };

        const sharesAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.shares,
            label: 'Mention.details.shares',
            value: this.mention.share,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-circle-nodes'
        };

        const repostsAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.shares,
            label: 'Mention.details.retweets',
            value: this.mention.share,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-arrows-retweet'
        };

        const viewsAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.views,
            label: 'Mention.details.views',
            value: this.mention.view,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-eye'
        };

        const quotesAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.quotes,
            label: 'Mention.details.quotes',
            value: this.mention.quote,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-quotes'
        };

        this.engagementInformation = [
            engagementAttribute,
            engagementRateAttribute,
        ];

        switch (this.mention.mediaType.id) {
            case MediaTypeEnum.TWITTER:
                this.engagementInformation.push(
                    repostsAttribute,
                    quotesAttribute,
                    repliesAttribute,
                    likesAttribute,
                    viewsAttribute
                );
                break;
            case MediaTypeEnum.INSTAGRAM:
                this.engagementInformation.push(
                    sharesAttribute,
                    commentsAttribute,
                    likesAttribute
                );
                break;
            case MediaTypeEnum.REDDIT:
                this.engagementInformation.push(
                    sharesAttribute,
                    commentsAttribute,
                    upvotesAttribute,
                    viewsAttribute
                );
                break;
            case MediaTypeEnum.LINKEDIN:
                this.engagementInformation.push(
                    commentsAttribute,
                    likesAttribute
                );
                break;
            case MediaTypeEnum.BLUESKY:
                this.engagementInformation.push(
                    likesAttribute,
                    quotesAttribute,
                    repliesAttribute,
                    repostsAttribute
                );
                break;
            default:
                this.engagementInformation.push(
                    sharesAttribute,
                    commentsAttribute,
                    likesAttribute,
                    viewsAttribute
                );
                break;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());

        if (this.regionRequest) {
            this.regionRequest.unsubscribe();
            this.regionRequest = null;
        }
    }
}
